/**
 * reusable login flow component
 *
 * On successful login this component forwards the the redirect route or to the root if there is no redirect.
 *
 * NOTE: upon reaching this page, user can toggle between /user/login and
 * /user/register without changing the original referring source route.
 */
// import primary libraries
import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useLocation, Link } from 'react-router-dom';
import { useDispatch } from 'react-redux';

// actions
import { sendLogin, sendGetLoginOptions } from '../authStore';
import { useLoggedInUser } from '../authService';

// utils
import apiUtils from '../../../global/utils/api';
import { isSafeUrl, safeRedirect } from '../../../global/utils/linkUtils.js';


// import user components
import { EmailInput, PasswordInput } from '../../../global/components/forms';
import Spinner from '../../../global/components/helpers/Spinner.jsx';
import StyledButton from 'shared-resources/components/src/StyledButton';

const UserLoginFlow = ({
  resetLink
  , loginRedirect
  , originRedirect
  , wrapperClass
  , logoUrl = 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png' // fall back to the pro-ficiency logo - does not work if null,
  , welcomeMessage = ''
}) => {

  const dispatch = useDispatch();
  const location = useLocation();

  const finalLogoUrl = logoUrl || 'https://pro-ficiency-static.s3.us-east-2.amazonaws.com/atlas/slp_full_blue.png';

  // check for email in params to pre-fill the username field on the login form
  const { email, error: rawSSOError } = apiUtils.objectFromQueryString(location.search);

  const [userEmail, setUserEmail] = useState(email || '');
  const [inputEmail, setInputEmail] = useState('');
  const [isEmailValid, setIsEmailValid] = useState(false);
  const [loginOptions, setLoginOptions] = useState(null);


  const submitUserEmail = (e) => {
    e?.preventDefault && e.preventDefault();
    setUserEmail(inputEmail);
  }

  const loggedInUser = useLoggedInUser();
  const [user, setUser] = useState({ username: email || '', password: '' });
  const [error, setError] = useState('');
  const [SSOError, setSSOError] = useState(null);

  const getLoginOptions = async () => {
    if (userEmail) {
      const { payload: loginOptions, error } = await dispatch(sendGetLoginOptions(userEmail));
      if (error) setError(error?.message || "There was a problem logging in. Please try again");
      setLoginOptions(loginOptions || {});
    }
  }

  useEffect(() => {
    if (userEmail) {
      setUser({ ...user, username: userEmail });
      getLoginOptions();
    }
  }, [userEmail]);

  // keep the SSO error in sync with the raw error from the query string but this way we can allow user to clear it as well
  useEffect(() => {
    if (rawSSOError !== SSOError) {
      setSSOError(rawSSOError);
    }
  }, [rawSSOError]);


  useEffect(() => {
    // this will run on mount and redirect if user is already logged in
    // it will also run on every update to loggedInUser (when the user logs in)
    if (loggedInUser) {
      // can't use react-router's history object here because we may be directing to an external site.
      // history.push(searchObject.redirect || "/");
      safeRedirect(loginRedirect || "/");
    }
  }, [loggedInUser, loginRedirect]);

  const handleSubmit = async (e) => {
    setError('');
    e.preventDefault();
    const { error } = await dispatch(sendLogin(user));
    // generic error message for security reasons
    if (error) {
      setError(error?.message || "There was a problem logging in. Please try again");
      // reset password field on error
      setUser({ ...user, password: '' });
    }
  }

  const resetForm = () => {
    setUser({ username: '', password: '' });
    setError('');
    setUserEmail('');
    setInputEmail('');
    setLoginOptions(null);
  }

  const forgotPasswordLink = (useCallback(() => {
    if (originRedirect) {
      // we have to encode the url to preserve its query string
      const loginLink = encodeURIComponent(originRedirect);
      return `/user/forgot-password${location.search ? location.search + `&email=${userEmail}&loginLink=${loginLink}` : `?email=${userEmail}&loginLink=${loginLink}`}`;
    } else {
      return `/user/forgot-password${location.search ? location.search + `&email=${userEmail}` : `?email=${userEmail}`}`;
    }
  }, [userEmail, originRedirect]))();

  if (loggedInUser) {
    // displayed while redirecting
    return <Spinner />;
  }

  return (
    <div className={wrapperClass}>
      {!userEmail ? (
        <>
          <div className='flex flex-col gap-5 px-2 w-full'>
            <div className='flex justify-center md:p-4'>
              <img className="w-auto h-auto max-h-[30px]" src={finalLogoUrl} alt='logo' />
            </div>
            <div className='flex flex-col md:gap-2'>
              <h1 className='text-neutral-sec text-3xl m-0'> Welcome to S+ Cloud! </h1>
              {welcomeMessage && <p className="text-sm">{welcomeMessage}</p>}
            </div>
            <form name='userLoginForm' onSubmit={submitUserEmail} className='w-full'>
              <label htmlFor='username' className='text-sm'>Sign in to access Simulations Plus cloud-enabled products and services.</label>
              <label htmlFor='username' className='mb-2 text-sm'>Enter your email to continue.</label>
              <EmailInput
                name='username'
                value={inputEmail}
                change={e => setInputEmail(e.target.value)}
                onValidationChange={setIsEmailValid}
                autoFocus={true}
                placeholder="name@company.com"
              />
              <StyledButton
                className='w-full'
                disabled={!inputEmail || !isEmailValid}
              >
                Continue &rarr;
              </StyledButton>
            </form>
          </div>
          {error && (
            <div className='bg-error border border-neutral-pri text-error p-1 m-1 rounded relative' role='alert'>
              <strong className='font-semibold'>Error! </strong>
              <span className='block sm:inline'>{error}</span>
            </div>
          )}
          {SSOError && (
            <div className='bg-error border border-neutral-pri text-error p-1 m-1 rounded relative' role='alert'>
              <span className='block sm:inline'>{getFriendlyError(SSOError)}</span>
              <button
                className='absolute top-0 bottom-0 right-0 px-4 py-3'
                type='button'
                onClick={(e) => {
                  e.preventDefault();
                  setSSOError(null);
                }}>
                <i className='fas fa-times'></i>
              </button>
            </div>
          )}
        </>
      ) : !loginOptions ? (
        <>
          <LoginOptionsHeader userEmail={userEmail} resetForm={resetForm} resetLink={resetLink} logoUrl={finalLogoUrl}>
            <p className='my-4'>Please wait while we look up your credentials...</p>
          </LoginOptionsHeader>
        </>
      ) : loginOptions?.accountLocked ? (
        <>
          <LoginOptionsHeader userEmail={userEmail} resetForm={resetForm} resetLink={resetLink} logoUrl={finalLogoUrl}>
            <div className='group bg-neutral-sec p-6 border border-transparent hover:border-neutral-pri rounded-lg hover:shadow flex flex-col gap-4 my-4 transition-all'>
              <p className='mb-4'>Your account is locked</p>
              <Link to={forgotPasswordLink} className='btn-login-muted w-full block group-hover:btn group-hover:w-full group-hover:block transition-all hover:no-underline'>Reset Password</Link>
            </div>
          </LoginOptionsHeader>
        </>
      ) : (
        <>
          <LoginOptionsHeader userEmail={userEmail} resetForm={resetForm} resetLink={resetLink} logoUrl={finalLogoUrl}>
            <p className='my-4 text-sm'>Choose from the options below</p>
          </LoginOptionsHeader>
          {error && (
            <div className='bg-error border border-neutral-pri text-error p-1 m-1 rounded relative' role='alert'>
              <strong className='font-semibold'>Error! </strong>
              <span className='block sm:inline'>{error}</span>
            </div>
          )}
          {loginOptions?.SSOLinks?.length > 0 && (
            <div className='group bg-neutral-sec border border-transparent hover:border-neutral-pri rounded-lg hover:shadow flex flex-col gap-1 p-2 m-1 transition-all'>
              <p className='text-neutral-sec group-hover:text-neutral-pri transition-all pb-2'>Sign in via</p>
              {loginOptions.SSOLinks.map((link, i) => (
                <StyledButton
                  key={link.name + '_' + i}
                  variant='primary'
                  className='w-full opacity-50 group-hover:!opacity-100'
                  href={loginRedirect ? link.link + `?RelayState=${encodeURIComponent(`${loginRedirect}`)}` : link.link}
                >
                  {link.name}
                </StyledButton>
              ))}
            </div>
          )}
          {loginOptions?.hasPassword ? (
            <div className='group bg-neutral-sec border border-transparent hover:border-neutral-pri rounded-lg hover:shadow flex flex-col gap-2 p-4 mb-2 transition-all'>
              <p className='text-neutral-sec group-hover:text-neutral-pri transition-all'>Enter Password</p>
              <form name='userLoginForm' onSubmit={handleSubmit} className='w-full'>
                <PasswordInput
                  name='password'
                  value={user.password}
                  change={e => setUser({ ...user, [e.target.name]: e.target.value })}
                  required={true}
                  className='bg-inherit focus:bg-neutral-pri group-hover:bg-neutral-pri transition-all'
                  autoFocus={true}
                />
                <StyledButton
                  variant='primary'
                  className='w-full'
                  type='submit'
                  disabled={!user.password}
                >
                  Sign in
                </StyledButton>
              </form>
              <Link to={forgotPasswordLink} className='text-neutral-sec group-hover:text-info transition-all w-fit'>Password trouble?</Link>
            </div>
          ) : loginOptions?.hasPassword === false ? (
            <div className='group bg-neutral-sec border border-transparent hover:border-neutral-pri rounded-lg hover:shadow flex flex-col gap-2 p-4 mb-2 transition-all'>
              <p className='text-neutral-sec group-hover:text-neutral-pri transition-all'>Looks like you don't have a password</p>
              <StyledButton
                variant='primary'
                className='w-full'
                to={forgotPasswordLink}
              >
                Set up password
              </StyledButton>
            </div>
          ) : null}
          {loginOptions?.allowMagicLink && (
            <div className='group bg-neutral-sec border border-transparent hover:border-neutral-pri rounded-lg hover:shadow flex flex-col gap-2 p-4 mb-2 transition-all'>
              <p className='text-neutral-sec group-hover:text-neutral-pri transition-all'>Sign in with a one-time link</p>
              <StyledButton
                variant='primary'
                className='w-full opacity-50 group-hover:!opacity-100'
                onClick={() => alert('TODO: This feature is not yet implemented')}
              >
                Send link to email
              </StyledButton>
            </div>
          )}
        </>
      )}
    </div>
  )
}

UserLoginFlow.propTypes = {
  resetLink: PropTypes.string
  , loginRedirect: PropTypes.string
  , originRedirect: PropTypes.string
  , wrapperClass: PropTypes.string
  , logoUrl: PropTypes.string
  , welcomeMessage: PropTypes.string
}

const LoginOptionsHeader = ({ userEmail, resetForm, resetLink, children, logoUrl }) => {

  return (
    <div className='flex flex-col gap-5 px-2'>
      <div className='flex justify-center p-2 md:p-4'>
        <img className="w-auto h-auto max-h-[30px]" src={logoUrl} alt='logo' />
      </div>
      <h1 className='text-neutral-sec text-3xl mt-0 mb-2'> Welcome to S+ Cloud! </h1>
      {resetLink ? (
        <Link className='flex justify-between text-xl text-neutral-pri mt-2 font-semibold hover:cursor-pointer hover:no-underline' to={isSafeUrl(resetLink)} onClick={resetForm}>
          <span>{userEmail}</span>
          <span className='text-neutral-ter my-auto text-xs transition-all ml-3'>
            <i className="fa-regular fa-square-x mr-1"></i>
            clear email
          </span>
        </Link>
      ) : (
        <div className='flex justify-between text-xl text-neutral-pri mt-2 font-semibold hover:cursor-pointer hover:no-underline' onClick={resetForm}>
          <span>{userEmail}</span>
          <span className='text-neutral-ter my-auto text-xs transition-all ml-3'>
            <i className="fa-regular fa-square-x mr-1"></i>
            clear email
          </span>
        </div>
      )}
      {children}
    </div>
  )
}

LoginOptionsHeader.propTypes = {
  userEmail: PropTypes.string
  , resetForm: PropTypes.func
  , resetLink: PropTypes.string
  , logoUrl: PropTypes.string
  , children: PropTypes.node
}


const getFriendlyError = (error) => {
  switch (error) {
    // ref returns on SessionController samlLogin and samlLoginCallback
    case 'provider_error_1':
      return 'Failed to Initiate SAML Login with Provider';
    case 'provider_error_2':
      return 'Error Authenticating with SAML Login Provider';
    case 'provider_error_3':
      return 'Failed to Authenticate with SAML Login Provider';
    case 'no_matching_user_1':
      return 'Unable to Locate Matching User. Please try again.';
    case 'no_matching_user_2':
      return 'Unable to Locate Matching User. Please try again.';
    case 'not_allowed_1':
      return 'Permission Denied. Please try again.';
    case 'not_allowed_2':
      return 'Unable To Log In As Global Admin Via SSO. Please Contact Admin.';
    default:
      return error;
  }
}

export default UserLoginFlow;
